import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts";
import {
  People as PeopleIcon,
  Assignment as TaskIcon,
  Schedule as ScheduleIcon,
  AccountBalance as BankIcon,
  Business as AgreementIcon,
  TrendingUp as TrendingIcon,
  Warning as WarningIcon,
  CheckCircle as CompletedIcon,
  Cancel as CanceledIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import { StatusGlobal } from "src/interface/enum/StatusGlobal";
import { FollowUpType } from "src/interface/enum/FollowUpType";
import { TransactionType } from "src/interface/enum/TransactionType";
import AdvancedStatistics from "./advanced-statistics";
import PerformanceSummary from "./performance-summary";

interface OverviewReportProps {
  users?: any[];
  tasks?: any[];
  followups?: any[];
  agreements?: any[];
  transactions?: any[];
  services?: any[];
  systems?: any[];
  isLoading?: boolean;
}

const COLORS = {
  primary: "#1976d2",
  secondary: "#dc004e",
  success: "#2e7d32",
  warning: "#ed6c02",
  error: "#d32f2f",
  info: "#0288d1",
  grey: "#757575",
};

const statusColors = {
  [StatusGlobal.NotStarted]: COLORS.grey,
  [StatusGlobal.Reported]: COLORS.info,
  [StatusGlobal.InProgress]: COLORS.warning,
  [StatusGlobal.PendingPayment]: COLORS.secondary,
  [StatusGlobal.Completed]: COLORS.success,
  [StatusGlobal.Completed2]: COLORS.success,
  [StatusGlobal.Canceled]: COLORS.error,
};

const statusLabels = {
  [StatusGlobal.NotStarted]: "انجام نشده",
  [StatusGlobal.Reported]: "گزارش داده",
  [StatusGlobal.InProgress]: "در حال انجام",
  [StatusGlobal.PendingPayment]: "در انتظار پرداخت",
  [StatusGlobal.Completed]: "انجام شده",
  [StatusGlobal.Canceled]: "لغو شده",
  [StatusGlobal.Completed2]: "انجام شده(دورکار)",
};

const followUpTypeLabels = {
  [FollowUpType.Weekly]: "هفتگی",
  [FollowUpType.Monthly]: "ماهیانه",
  [FollowUpType.Fasli]: "فصلی",
  [FollowUpType.Yearly]: "سالیانه",
};

const transactionTypeLabels = {
  [TransactionType.Contractual]: "قراردادی",
  [TransactionType.Systemic]: "سیستمی",
  [TransactionType.Manual]: "دستی",
};

export default function OverviewReport({
  users = [],
  tasks = [],
  followups = [],
  agreements = [],
  transactions = [],
  services = [],
  systems = [],
  isLoading = false,
}: OverviewReportProps) {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  // محاسبه آمار کلی
  const statistics = useMemo(() => {
    // اطمینان از اینکه همه داده‌ها آرایه هستند
    const safeUsers = Array.isArray(users) ? users : [];
    const safeTasks = Array.isArray(tasks) ? tasks : [];
    const safeFollowups = Array.isArray(followups) ? followups : [];
    const safeAgreements = Array.isArray(agreements) ? agreements : [];
    const safeTransactions = Array.isArray(transactions) ? transactions : [];
    const safeServices = Array.isArray(services) ? services : [];
    const safeSystems = Array.isArray(systems) ? systems : [];

    const totalUsers = safeUsers.length;
    const totalTasks = safeTasks.length;
    const totalFollowups = safeFollowups.length;
    const totalAgreements = safeAgreements.length;
    const totalTransactions = safeTransactions.length;
    const totalServices = safeServices.length;
    const totalSystems = safeSystems.length;

    // آمار کارها بر اساس وضعیت
    const taskStatusStats = safeTasks.reduce(
      (acc, task) => {
        const status = task.statusTask || StatusGlobal.NotStarted;
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as Record<StatusGlobal, number>
    );

    // آمار پیگیری‌ها بر اساس نوع
    const followupTypeStats = safeFollowups.reduce(
      (acc, followup) => {
        const type = followup.followUpType || FollowUpType.Monthly;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<FollowUpType, number>
    );

    // آمار تراکنش‌ها بر اساس نوع
    const transactionTypeStats = safeTransactions.reduce(
      (acc, transaction) => {
        const type = transaction.transactionType || TransactionType.Manual;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<TransactionType, number>
    );

    // آمار تراکنش‌ها بر اساس وضعیت پرداخت
    const paymentStatusStats = safeTransactions.reduce(
      (acc, transaction) => {
        const status = transaction.transactionStatus || 0;
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as Record<number, number>
    );

    // محاسبه درآمد کل
    const totalRevenue = safeTransactions
      .filter((t) => t.transactionStatus === 1) // پرداخت شده
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    // آمار ماهانه
    const monthlyStats = Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const monthAgreements = safeAgreements.filter((a) => {
        const createdDate = new Date(a.createdOn || "");
        return createdDate.getMonth() + 1 === month;
      });
      const monthRevenue = safeTransactions
        .filter((t) => {
          const transactionDate = new Date(t.transactionDate || "");
          return (
            transactionDate.getMonth() + 1 === month &&
            t.transactionStatus === 1
          );
        })
        .reduce((sum, t) => sum + (t.amount || 0), 0);

      return {
        month: `${month}ماه`,
        agreements: monthAgreements.length,
        revenue: monthRevenue,
      };
    });

    return {
      totalUsers,
      totalTasks,
      totalFollowups,
      totalAgreements,
      totalTransactions,
      totalServices,
      totalSystems,
      totalRevenue,
      taskStatusStats,
      followupTypeStats,
      transactionTypeStats,
      paymentStatusStats,
      monthlyStats,
    };
  }, [users, tasks, followups, agreements, transactions, services, systems]);

  // داده‌های چارت وضعیت کارها
  const taskStatusData = useMemo(() => {
    return Object.entries(statistics.taskStatusStats).map(
      ([status, count]) => ({
        name: statusLabels[Number(status) as StatusGlobal],
        value: count,
        color: statusColors[Number(status) as StatusGlobal],
      })
    );
  }, [statistics.taskStatusStats]);

  // داده‌های چارت نوع پیگیری‌ها
  const followupTypeData = useMemo(() => {
    return Object.entries(statistics.followupTypeStats).map(
      ([type, count]) => ({
        name: followUpTypeLabels[Number(type) as FollowUpType],
        value: count,
      })
    );
  }, [statistics.followupTypeStats]);

  // داده‌های چارت نوع تراکنش‌ها
  const transactionTypeData = useMemo(() => {
    return Object.entries(statistics.transactionTypeStats).map(
      ([type, count]) => ({
        name: transactionTypeLabels[Number(type) as TransactionType],
        value: count,
      })
    );
  }, [statistics.transactionTypeStats]);

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, textAlign: "center" }}>
        گزارش کلی از برنامه
      </Typography>

      {/* تب‌های گزارش */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="report tabs"
        >
          <Tab label="نمای کلی" />
          <Tab label="آمار پیشرفته" />
          <Tab label="خلاصه عملکرد" />
        </Tabs>
      </Box>

      {/* محتوای تب نمای کلی */}
      {activeTab === 0 && (
        <>
          {/* کارت‌های آمار کلی */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Avatar sx={{ bgcolor: COLORS.primary, mx: "auto", mb: 2 }}>
                    <PeopleIcon />
                  </Avatar>
                  <Typography variant="h4" color="primary">
                    {statistics.totalUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    کل کاربران
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Avatar sx={{ bgcolor: COLORS.success, mx: "auto", mb: 2 }}>
                    <AgreementIcon />
                  </Avatar>
                  <Typography variant="h4" color="success.main">
                    {statistics.totalAgreements}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    کل قراردادها
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Avatar sx={{ bgcolor: COLORS.warning, mx: "auto", mb: 2 }}>
                    <TaskIcon />
                  </Avatar>
                  <Typography variant="h4" color="warning.main">
                    {statistics.totalTasks}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    کل کارها
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Avatar sx={{ bgcolor: COLORS.info, mx: "auto", mb: 2 }}>
                    <TrendingIcon />
                  </Avatar>
                  <Typography variant="h4" color="info.main">
                    {statistics.totalRevenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    درآمد کل (تومان)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* چارت‌های مختلف */}
          <Grid container spacing={3}>
            {/* چارت وضعیت کارها */}
            <Grid xs={12} md={6}>
              <Paper sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom>
                  وضعیت کارها
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={taskStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${((percent || 0) * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {taskStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* چارت نوع پیگیری‌ها */}
            <Grid xs={12} md={6}>
              <Paper sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom>
                  نوع پیگیری‌ها
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={followupTypeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill={COLORS.primary} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* چارت آمار ماهانه */}
            <Grid xs={12}>
              <Paper sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom>
                  آمار ماهانه قراردادها و درآمد
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={statistics.monthlyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="agreements"
                      stackId="1"
                      stroke={COLORS.primary}
                      fill={COLORS.primary}
                      name="قراردادها"
                    />
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenue"
                      stackId="2"
                      stroke={COLORS.success}
                      fill={COLORS.success}
                      name="درآمد (تومان)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* چارت نوع تراکنش‌ها */}
            <Grid xs={12} md={6}>
              <Paper sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom>
                  نوع تراکنش‌ها
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={transactionTypeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill={COLORS.secondary} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* آمار اضافی */}
            <Grid xs={12} md={6}>
              <Paper sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom>
                  آمار اضافی
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      کل پیگیری‌ها
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {statistics.totalFollowups}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      کل تراکنش‌ها
                    </Typography>
                    <Typography variant="h4" color="secondary">
                      {statistics.totalTransactions}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      کل خدمات
                    </Typography>
                    <Typography variant="h4" color="info.main">
                      {statistics.totalServices}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      کل سامانه‌ها
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {statistics.totalSystems}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* محتوای تب آمار پیشرفته */}
      {activeTab === 1 && (
        <AdvancedStatistics
          tasks={tasks}
          followups={followups}
          agreements={agreements}
          transactions={transactions}
          services={services}
          isLoading={isLoading}
        />
      )}

      {/* محتوای تب خلاصه عملکرد */}
      {activeTab === 2 && (
        <PerformanceSummary
          tasks={tasks}
          followups={followups}
          agreements={agreements}
          transactions={transactions}
          services={services}
          isLoading={isLoading}
        />
      )}
    </Box>
  );
}
